import {
  Goal,
  GroupByMode,
  GroupedSection,
  GroupSectionType,
  ID,
  LifeArea,
  noPriorityOption,
  SortingMode,
  Tag,
  Task,
  TaskPriorityOptions,
  Timestamp,
  WeekDays,
} from '@griply/types';

import { getLinkedItemColor } from './get-linked-item-color';
import { getSortedTasks } from './get-sorted-tasks';
import { groupTasksByDeadline } from './group-tasks-by-deadline';
import { groupTasksByDeadlineDeadline } from './group-tasks-by-deadline-deadline';
import { groupTasksByGoal } from './group-tasks-by-goal';
import { groupTasksByLifeArea } from './group-tasks-by-life-area';
import { groupTasksByNone } from './group-tasks-by-none';
import { groupTasksByPriority } from './group-tasks-by-priority';
import { groupTasksByTag } from './group-tasks-by-tag';
import { groupTasksByType } from './group-tasks-by-type';

const PRIORITY_LABELS: Record<string, string> = {
  [TaskPriorityOptions.High]: 'High',
  [TaskPriorityOptions.Medium]: 'Medium',
  [TaskPriorityOptions.Low]: 'Low',
  [noPriorityOption]: 'None',
};

export type BuildGroupedSortedSectionsOptions = {
  tasks: Task[];
  groupByMode: GroupByMode;
  sortingMode?: SortingMode | null;
  customOrder?: Record<ID, ID[]> | ID[];
  lifeAreas: LifeArea[];
  goals: Goal[];
  tags: Tag[];
  taskTagsMap: Map<ID, ID[]>;
  lifeAreaSorting?: ID[];
  goalSorting?: ID[];
  tagSorting?: ID[];
  today: Date;
  weekStartsOn: WeekDays;
  dateFilterEndDate?: Timestamp | null;
};

const buildSections = ({
  tasks,
  groupByMode,
  lifeAreas,
  goals,
  tags,
  taskTagsMap,
  lifeAreaSorting,
  goalSorting,
  tagSorting,
  today,
  weekStartsOn,
  dateFilterEndDate,
}: BuildGroupedSortedSectionsOptions): GroupedSection[] => {
  if (groupByMode === GroupByMode.None) {
    return groupTasksByNone(tasks);
  }

  if (groupByMode === GroupByMode.LifeArea) {
    const groups = groupTasksByLifeArea({
      tasks,
      lifeAreas,
      lifeAreaSorting,
      goals,
    });
    return groups.map((g) => ({
      sectionId: g.lifeAreaId,
      sectionType: GroupSectionType.LifeArea,
      name: g.lifeArea?.name ?? 'Other',
      iconName: g.lifeArea?.iconName ?? 'user-select-placeholder',
      imageUrl: g.lifeArea?.visionPreview?.image?.url ?? null,
      color: g.lifeArea?.color ?? null,
      tasks: g.tasks,
    }));
  }

  if (groupByMode === GroupByMode.Goal) {
    const groups = groupTasksByGoal({ tasks, goals, goalSorting });
    return groups.map((g) => ({
      sectionId: g.goalId,
      sectionType: GroupSectionType.Goal,
      name: g.goal?.name ?? 'Other',
      iconName: g.goal?.iconName ?? undefined,
      imageUrl: g.goal?.image?.url ?? null,
      color: g.goal
        ? getLinkedItemColor(g.goal.id, { goals, lifeAreas })
        : null,
      tasks: g.tasks,
    }));
  }

  if (groupByMode === GroupByMode.Priority) {
    const groups = groupTasksByPriority({ tasks });
    return groups.map((g) => ({
      sectionId: g.priority,
      sectionType: GroupSectionType.Priority,
      name: PRIORITY_LABELS[g.priority] ?? g.priority,
      tasks: g.tasks,
    }));
  }

  if (groupByMode === GroupByMode.Type) {
    return groupTasksByType(tasks);
  }

  if (groupByMode === GroupByMode.Tag) {
    return groupTasksByTag({ tasks, tags, taskTagsMap, tagSorting });
  }

  if (groupByMode === GroupByMode.Deadline) {
    return groupTasksByDeadline({
      tasks,
      today,
      weekStartsOn,
      dateFilterEndDate,
    });
  }

  if (groupByMode === GroupByMode.DeadlineDeadline) {
    return groupTasksByDeadlineDeadline(tasks, today);
  }

  return groupTasksByNone(tasks);
};

export const buildGroupedSortedSections = (
  options: BuildGroupedSortedSectionsOptions,
): GroupedSection[] => {
  const { sortingMode, customOrder, groupByMode, today, weekStartsOn } =
    options;

  const sections = buildSections(options);

  // Sort tasks within each section
  return sections.map((section) => {
    const orderForSection =
      sortingMode === SortingMode.Custom
        ? groupByMode === GroupByMode.None
          ? (customOrder as ID[] | undefined)
          : (customOrder as Record<ID, ID[]> | undefined)?.[section.sectionId]
        : undefined;

    const sortedTasks = getSortedTasks(section.tasks, {
      today,
      weekStartsOn,
      order: orderForSection ?? [],
      mode: sortingMode ? [SortingMode.DueDate, sortingMode] : undefined,
    });

    return { ...section, tasks: sortedTasks };
  });
};
